import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useUser, UserRole } from '../lib/userContext';
import { PageLoader } from './PageLoader';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
  fallback?: string;
  unauthorizedFallback?: string;
}

export function ProtectedRoute({ 
  children, 
  allowedRoles,
  fallback = '/login',
  unauthorizedFallback = '/dashboard'
}: ProtectedRouteProps) {
  const { currentUser, loading, initialized } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    // Wait for auth to be initialized
    if (!initialized || loading) {
      return;
    }

    if (!currentUser?.id) {
      navigate(fallback, { replace: true });
    } else if (currentUser.is_active === false) {
      // Redirect inactive users to login
      navigate(fallback, { replace: true });
    } else if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
      navigate(unauthorizedFallback, { replace: true });
    }
  }, [currentUser, loading, initialized, navigate, allowedRoles, fallback, unauthorizedFallback]);

  // Show loader while initializing or loading
  if (!initialized || loading) {
    return <PageLoader />;
  }

  // Don't render anything if not authenticated or not authorized
  if (!currentUser?.id) {
    return null;
  }

  if (currentUser.is_active === false) {
    return null;
  }

  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    return null;
  }

  // Render children immediately when auth checks pass
  return <>{children}</>;
}

export default ProtectedRoute;
